import { useCallback, useEffect, useRef } from "react";
import { useAtom, useAtomValue, useSetAtom } from "jotai";

import {
  autoLayoutAtom,
  clearCanvasAtom,
  createNotificationAtom,
  cursorAtom,
  nodesAtom,
  sideBarSelectedAtom,
  saveCanvasAsyncCommand,
  selectedNodeTypeAtom,
} from "@zapier/canvas-state";
import { useCenterAndZoomOnNode } from "../../hooks/useCenterAndZoomOnNode";
import { AutoLayoutSuccessMessage } from "../AutoLayoutSuccessMessage";

export default function KeydownListeners() {
  const clearCanvas = useSetAtom(clearCanvasAtom);
  const { centerAndZoomOnNode } = useCenterAndZoomOnNode();
  const setAutoLayout = useSetAtom(autoLayoutAtom);
  const saveToBackend = useSetAtom(saveCanvasAsyncCommand);
  const createNotification = useSetAtom(createNotificationAtom);
  const currentNodes = useAtomValue(nodesAtom);
  const [cursorMode, setCursorMode] = useAtom(cursorAtom);
  const [selectedNodeType, setSelectedNodeType] = useAtom(selectedNodeTypeAtom);

  const [sideBarSelected, setToolbarSelected] = useAtom(sideBarSelectedAtom);

  const wasRepeating = useRef(false);

  const runAutoLayoutAndSave = useCallback(async () => {
    if (currentNodes.length === 0) return;

    const { nodes, isSuccess } = setAutoLayout();

    if (isSuccess) {
      saveToBackend({ nodes });

      createNotification({
        type: "success",
        message: <AutoLayoutSuccessMessage />,
      });
    }
  }, [setAutoLayout, createNotification, saveToBackend, currentNodes.length]);

  const toggleDevTools = useCallback(() => {
    if (sideBarSelected === "devTools") {
      setToolbarSelected(null);
    } else {
      setToolbarSelected("devTools");
    }
  }, [sideBarSelected, setToolbarSelected]);

  const isInputField = (target: EventTarget | null) => {
    return (
      target instanceof HTMLInputElement ||
      target instanceof HTMLTextAreaElement ||
      (target as HTMLElement)?.isContentEditable
    );
  };

  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      // Cmd/Ctrl + Shift + C for clearing the canvas
      if (
        (event.metaKey || event.ctrlKey) &&
        event.shiftKey &&
        event.code === "KeyC"
      ) {
        event.preventDefault(); // Prevent any default browser behavior
        void clearCanvas(centerAndZoomOnNode);
      }

      // Cmd/Ctrl + Shift + Y for applying auto layout
      if (
        (event.metaKey || event.ctrlKey) &&
        event.shiftKey &&
        event.code === "KeyY"
      ) {
        event.preventDefault(); // Prevent any default browser behavior
        runAutoLayoutAndSave();
      }

      // Cmd/Ctrl + D for toggling DevTools
      if ((event.metaKey || event.ctrlKey) && event.code === "KeyD") {
        event.preventDefault(); // Prevent any default browser behavior
        toggleDevTools();
      }

      if (event.code === "Space" && !isInputField(event.target)) {
        wasRepeating.current = event.repeat;
        // Only trigger when key is being held
        if (event.repeat) {
          if (selectedNodeType) {
            setSelectedNodeType(null);
          }

          setCursorMode("pan");
        }
      }
    }

    const handleKeyUp = (event: KeyboardEvent) => {
      if (event.code === "Space" && !isInputField(event.target)) {
        // Only reset to select if we were in pan mode from holding space
        if (wasRepeating.current && cursorMode === "pan") {
          setCursorMode("select");
        }
        wasRepeating.current = false;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);

    // Clean up the event listener
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    toggleDevTools,
    runAutoLayoutAndSave,
    cursorMode,
    setCursorMode,
    selectedNodeType,
    setSelectedNodeType,
  ]);

  return null;
}
